/**
 * Labour rate DEFAULTS — shared client/server (Wave 1).
 *
 * One source for the default rate constants that were previously mirrored
 * across src/utils/labourRates.ts (client canonical), quote-pricing,
 * functions/src/xero/push-invoice.ts, functions/src/proposals/
 * _acceptance-resolution.ts, and functions/src/mcp/sync.ts.
 *
 * ⚠ OSCILLATION RULE (fault rate): client recalculateCosts and the MCP
 * sync BOTH recompute fault.costs.labourCost over CUMULATIVE labourMinutes.
 * Changing the effective rate on one side makes the stored figure oscillate
 * between surfaces, not just go stale. With one shared constant that class
 * dies — but the same rule applies to the FUTURE tenant-override work
 * (TODO(saas-readiness)): thread `tenant.business.labourRates` into BOTH
 * sides' call graphs in one change, never partially.
 *
 * Tenant resolvers (getQuoteLabourRates etc.) stay client-side in
 * labourRates.ts — they need TenantConfig; these are the pure defaults.
 */

/** Quote labour team configurations (proposal pricing sell rates). */
export type QuoteLabourTeam = '1-tech' | '2-techs' | '2-senior-techs';

export interface QuoteLabourRates {
  '1-tech': number;
  '2-techs': number;
  '2-senior-techs': number;
}

/**
 * Default quote rates (AUD/hour, ex GST) — Techery's AU market starting
 * point. Used when a tenant has no override in
 * `tenant.business.labourRates.quote`.
 */
export const DEFAULT_QUOTE_LABOUR_RATES: QuoteLabourRates = {
  '1-tech': 90,
  '2-techs': 160,
  '2-senior-techs': 200,
};

/**
 * Default fault-cost labour rate (AUD/hour, ex GST) — internal cost rate
 * for fault cost rollups + project cost-side math. Override:
 * `tenant.business.labourRates.faultPerHour` (convert BOTH sides together —
 * see the oscillation rule above).
 */
export const DEFAULT_FAULT_LABOUR_RATE_PER_HOUR = 120;

/**
 * Default client-facing labour sell rate (AUD/hour, ex GST) — roughly 2×
 * the cost rate. Override: `tenant.business.labourRates.sellPerHour`.
 */
export const DEFAULT_LABOUR_SELL_RATE_PER_HOUR = 240;
